/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { GetWineFilterable } from './get-wine-filterable-class';
import { WineMapper } from './get-wine-mapper-class';

// =============================================================================
// API CONSTRUCTION
// =============================================================================
/**
 * Class Definition for constructing the wine location query
 */
export class GetWineLocationInput extends GetWineFilterable<IGetWineLocationQueryFilters> {

    private readonly _url: string = 'https://api.smwe.com/v1/XXX/retailers';

    get url(): string {
        return this._url;
    }
}

// =============================================================================
// API MAPPER
// =============================================================================

enum GetWineLocationMap {
    totalResults = 'total_results',
    totalPages = 'total_pages',
    page = 'page'
}

enum GetWineLocationRetailerMap {
    code = 'code',
    name = 'name',
    address = 'address',
    city = 'city',
    state = 'state',
    zip = 'zip',
    lon = 'lon',
    lat = 'lat',
    onPremise = 'on_premise',
    distance = 'distance'
}

enum GetWineLocationProductMap {
    productKey = 'product_key',
    brandCode = 'brand_code',
    productCode = 'product_code',
    description = 'description',
}

/**
 * Class definition for the output controller of the API. Acts as a middle layer
 * and ensures that the data stays consistent internally
 */
class GetWineLocationMapper extends WineMapper<IGetWineLocationDataInput> {
    public get output(): IGetWineLocationDataOutput {
        const output: IGetWineLocationDataOutput = {
            data: [],
            totalPages: this._input[GetWineLocationMap.totalPages],
            totalResults: this._input[GetWineLocationMap.totalResults],
            page: this._input[GetWineLocationMap.page]
        };

        this._input.retailers.forEach(retailer => {
            const retailerProducts: IGetWineLocationProductOutput[] = [];
            const mappedRetailer =
                this._applyMap<IGetWineLocationRetailerOutput, IGetWineLocationRetailerInput, typeof GetWineLocationRetailerMap>(retailer, GetWineLocationRetailerMap);
            mappedRetailer.products = retailerProducts;
            output.data.push(mappedRetailer);

            retailer.products.forEach(product => {
                retailerProducts.push(
                    this._applyMap(product, GetWineLocationProductMap)
                );
            });
        });

        return output;
    }
}

// =============================================================================
// WINE LOCATION INTERFACE OUTPUT
// =============================================================================

export interface IGetWineLocationDataOutput {
    'totalResults': number;
    'totalPages': number;
    'page': number;
    'data': IGetWineLocationRetailerOutput[];
}

export interface IGetWineLocationRetailerOutput {
    'code': number;
    'name': string;
    'address': string;
    'city': string;
    'state': string;
    'zip': string;
    'lon': number;
    'lat': number;
    'onPremise': boolean;
    'distance': number;
    'products': IGetWineLocationProductOutput[];
}

export interface IGetWineLocationProductOutput {
    'productKey': string;
    'brandCode': string;
    'productCode': string;
    'description': string;
}

// =============================================================================
// WINE LOCATION INTERFACE INPUT
// =============================================================================

interface IGetWineLocationDataInput {
    'total_results': number;
    'total_pages': number;
    'page': number;
    'retailers': IGetWineLocationRetailerInput[];
}

interface IGetWineLocationRetailerInput {
    'code': number;
    'name': string;
    'address': string;
    'city': string;
    'state': string;
    'zip': string;
    'lon': number;
    'lat': number;
    'on_premise': boolean;
    'distance': number;
    'products': IGetWineLocationProductInput[];
}

interface IGetWineLocationProductInput {
    'product_key': string;
    'brand_code': string;
    'product_code': string;
    'description': string;
}

/**
 * query filter definition
 */
export interface IGetWineLocationQueryFilters {
    product?: string;
    brand?: string;
    radius?: number;
    pagination?: boolean;
    page?: number;
    per_page?: number;
    on_premise?: boolean;
    location: string;
}

// =============================================================================
// CREATE INPUT
// =============================================================================
/**
 * constructs a wine location query and returns it to the action
 */
const createWineLocationInput = (args: Msdyn365.ICreateActionContext<IGetWineLocationQueryFilters>): Msdyn365.IActionInput => {
    if (!args.config) { throw new Error(`[createWineLocationInput] configuration was invalid: ${args.config}`); }
    return new GetWineLocationInput(args.config);
};

// =============================================================================
// RUN INPUT / ACTION
// =============================================================================
/**
 * api runner
 * @returns the location data
 */
async function getWineLocationAction(input: GetWineLocationInput, ctx: Msdyn365.IActionContext): Promise<IGetWineLocationDataOutput> {
    const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    // query request and return response full of locations to purchase wine
    const response = await Msdyn365.sendRequest<IGetWineLocationDataInput>(`${input.url}${input.filtersToHTTPQueryString()}`, 'get', apiSettings, {
        headers: {
            Authorization: `Token token="${input.token}"`
        }
    });
    return new GetWineLocationMapper(response.data).output;
}

export default Msdyn365.createDataAction({
    action: <Msdyn365.IAction<IGetWineLocationDataOutput>>getWineLocationAction,
    input: createWineLocationInput
});
